import type { Inventory, ItemId } from '../state/types';
import type { IconToken } from '../icons/index';

export interface ItemDef {
  id: ItemId;
  name: string;
  icon: IconToken;
  /** Hotkey that uses the item, if any. */
  key?: string;
  desc: string;
}

export const ITEMS: Record<ItemId, ItemDef> = {
  beer: {
    id: 'beer',
    name: 'Nattie Lite',
    icon: ':beer:',
    key: 'Q',
    desc: 'Crack a cold one. +Buzz, +1 empty can.',
  },
  hotdog: {
    id: 'hotdog',
    name: 'Roller Dog',
    icon: ':hotdog:',
    key: 'F',
    desc: 'Been rolling since Tuesday.',
  },
  porkRinds: {
    id: 'porkRinds',
    name: 'Pork Rinds',
    icon: ':pork-rinds:',
    key: 'F',
    desc: 'Zero carbs. Some regrets.',
  },
  sushi: {
    id: 'sushi',
    name: 'Gas Station Sushi',
    icon: ':sushi:',
    key: 'F',
    desc: 'Sold next to the motor oil.',
  },
  dewd: {
    id: 'dewd',
    name: 'Mtn Dewd',
    icon: ':mtn-dewd:',
    key: 'R',
    desc: 'Neon green rocket fuel. +Energy.',
  },
  scratcher: {
    id: 'scratcher',
    name: 'Scratch-Off',
    icon: ':scratcher:',
    key: 'T',
    desc: 'Your retirement plan.',
  },
  fireworks: {
    id: 'fireworks',
    name: 'Fireworks',
    icon: ':fireworks:',
    key: 'G',
    desc: 'Freedom, in a cardboard tube.',
  },
  cans: { id: 'cans', name: 'Empty Cans', icon: ':empty-can:', desc: 'Junkyard Jerry pays 50¢ a can.' },
  scrap: { id: 'scrap', name: 'Scrap Metal', icon: ':scrap:', desc: "Somebody's trash. Jerry's treasure." },
  parts: { id: 'parts', name: 'Car Parts', icon: ':gear:', desc: 'For the Camaro. Allegedly compatible.' },
  catfish: {
    id: 'catfish',
    name: 'Channel Cat',
    icon: ':catfish:',
    desc: 'Fresh from the crick. Grill it at home, or sell it to Big Earl for his fish fry.',
  },
  copper: {
    id: 'copper',
    name: 'Copper Wire (lb)',
    icon: ':copper-wire:',
    desc: 'Stripped clean. Junkyard Jerry pays top dollar by the pound.',
  },
  shine: {
    id: 'shine',
    name: 'Moonshine',
    icon: ':moonshine:',
    desc: 'Lightning in a jar. Drink it (careful now) or sell it to Trenchcoat Terry.',
  },
  mash: {
    id: 'mash',
    name: 'Sweet Feed Mash',
    icon: ':corn:',
    desc: "Corn, sugar and yeast. Uncle Rudy's still eats one sack per batch.",
  },
  cigs: {
    id: 'cigs',
    name: 'Paul Mauls (Smokes)',
    icon: ':smokes:',
    desc: 'A smoke break: a little energy, a little less health, a lot of standing around.',
  },
  wine: {
    id: 'wine',
    name: "Bone's Farm Strawberry Hill",
    icon: ':strawberry-hill:',
    desc: 'Fortified fruit wine. Tastes like a Jolly Rancher that hates you. Big buzz.',
  },
  whiskey: {
    id: 'whiskey',
    name: 'Fire Bawl Shooter',
    icon: ':fire-bawl:',
    desc: 'Cinnamon whisky in a tiny bottle. A shot of courage and a shot of regret.',
  },
  scratchCow: {
    id: 'scratchCow',
    name: 'Cash Cow Scratcher',
    icon: ':cow:',
    desc: '$5 ticket. Top prize $2,500. Udderly unlikely.',
  },
  scratchGator: {
    id: 'scratchGator',
    name: 'Gator Gold Scratcher',
    icon: ':gator:',
    desc: '$10 ticket. Top prize $10,000. Snap out of it.',
  },
  scratchMega: {
    id: 'scratchMega',
    name: 'Trailer Park Millionaire',
    icon: ':gem:',
    desc: '$20 ticket. Top prize $100,000. Darlene says she knew a guy.',
  },
  tvDinner: {
    id: 'tvDinner',
    name: 'Hungry-Man XL TV Dinner',
    icon: ':tv-dinner:',
    key: 'F',
    desc: 'Salisbury steak, mystery corn and a brownie that tastes like the steak. +60 Hunger, +5 Health.',
  },
  pizza: {
    id: 'pizza',
    name: 'Gas-N-Grub Whole Pizza',
    icon: ':pizza:',
    key: 'F',
    desc: 'The cheese has a skin on it. +75 Hunger, +5 Energy.',
  },
  chicken: {
    id: 'chicken',
    name: '12-Piece Bucket of Chicken',
    icon: ':fried-chicken:',
    key: 'F',
    desc: 'Meant for a family. You are a family of one. Fills you all the way up, +10 Health.',
  },
  bigEnergy: {
    id: 'bigEnergy',
    name: "Liquid Lightnin' 24oz",
    icon: ':energy-drink:',
    key: 'R',
    desc: 'Tastes like a battery dipped in Skittles. +55 Energy, -3 Health.',
  },
  rocketFuel: {
    id: 'rocketFuel',
    name: 'Redneck Rocket Fuel Shot',
    icon: ':energy-shot:',
    key: 'R',
    desc: 'Five hours of energy in two seconds. +85 Energy, sobers you up some, -8 Health. Your heart has notes.',
  },
  flashlight: {
    id: 'flashlight',
    name: 'Mag-Lite Flashlight',
    icon: ':flashlight:',
    key: 'L',
    desc: 'Four D batteries and a heft that says "I could also hit you with this." L turns it on.',
  },
  headlamp: {
    id: 'headlamp',
    name: "Coon-Huntin' Headlamp",
    icon: ':headlamp:',
    key: 'L',
    desc: 'A big beam strapped to your forehead, hands free for a beer. L turns it on.',
  },
  foilHat: {
    id: 'foilHat',
    name: "Hank's Tinfoil Hat",
    icon: ':foil-hat:',
    desc: 'Blocks their signals. Or boosts them. Hank says science is "still out." Click to put it on or take it off.',
  },
  alienGizmo: {
    id: 'alienGizmo',
    name: 'Glowing Doohickey',
    icon: ':ufo:',
    desc: "You brought it back from... somewhere. It hums. Hank would pay good money. So would Trenchcoat Terry, but don't.",
  },
  rascal: {
    id: 'rascal',
    name: "Lil' Rascal Mobility Scooter",
    icon: ':rascal:',
    key: 'B',
    desc: 'Three wheels, a basket, a cup holder and a flag. B (or E by it) hops on; Space hops off.',
  },
};

/** Lights you can carry, weakest first (the best one you own is the one you use). */
// (Hank's hat and the thing from the sky are below.)
export const LAMP_ITEMS: readonly ItemId[] = ['flashlight', 'headlamp'];

/** Every kind of scratch-off, cheapest first. */
export const SCRATCH_ITEMS: readonly ItemId[] = ['scratcher', 'scratchCow', 'scratchGator', 'scratchMega'];

/** Foods in the order the "eat" hotkey reaches for them (sushi last, for obvious reasons). */
export const FOOD_PRIORITY: readonly ItemId[] = [
  'hotdog',
  'porkRinds',
  'tvDinner',
  'pizza',
  'chicken',
  'sushi',
];

/** How much hunger each food fills. */
export const FOOD_HUNGER: Partial<Record<ItemId, number>> = {
  porkRinds: 15,
  hotdog: 35,
  sushi: 50,
  tvDinner: 60,
  pizza: 75,
  chicken: 100,
};

/** Energy drinks and how much they give. */
export const ENERGY_DRINKS: Partial<Record<ItemId, number>> = { dewd: 30, bigEnergy: 55, rocketFuel: 85 };

/**
 * The food (or drink) that best fits what you're missing: a snack when you're peckish,
 * the bucket of chicken when you're starving. Sushi is a last resort.
 */
export function bestFit(
  owned: readonly ItemId[],
  values: Partial<Record<ItemId, number>>,
  missing: number,
): ItemId | null {
  let best: ItemId | null = null;
  let bestScore = Infinity;
  for (const id of owned) {
    const v = values[id];
    if (v === undefined) continue;
    // Wasting some is better than coming up short; sushi is a gamble.
    const over = Math.max(0, v - missing);
    const under = Math.max(0, missing - v);
    const score = over * 0.6 + under + (id === 'sushi' ? 25 : 0);
    if (score < bestScore) {
      bestScore = score;
      best = id;
    }
  }
  return best;
}

export interface ShopEntry {
  id: string;
  name: string;
  icon: IconToken;
  price: number;
  desc: string;
  give: Partial<Inventory>;
}

export const GAS_STATION_STOCK: readonly ShopEntry[] = [
  {
    id: 'beer12',
    name: 'Nattie Lite 12-Pack',
    icon: ':beer-case:',
    price: 11,
    give: { beer: 12 },
    desc: "Lukewarm by the time you're home. Still counts.",
  },
  {
    id: 'tallboy',
    name: 'Nattie Lite Tallboy',
    icon: ':beer:',
    price: 1.5,
    give: { beer: 1 },
    desc: 'For the budget-conscious connoisseur.',
  },
  {
    id: 'hotdog',
    name: 'Roller Grill Hot Dog',
    icon: ':hotdog:',
    price: 1.5,
    give: { hotdog: 1 },
    desc: 'Which Tuesday? Unclear. +35 Hunger.',
  },
  {
    id: 'rinds',
    name: 'Pork Rinds',
    icon: ':pork-rinds:',
    price: 1.29,
    give: { porkRinds: 1 },
    desc: 'Crunchy air with ambition. +15 Hunger.',
  },
  {
    id: 'sushi',
    name: 'Gas Station Sushi',
    icon: ':sushi:',
    price: 4.99,
    give: { sushi: 1 },
    desc: '+50 Hunger. 35% chance of a religious experience.',
  },
  {
    id: 'dewd',
    name: 'Mtn Dewd 2-Liter',
    icon: ':mtn-dewd:',
    price: 2.29,
    give: { dewd: 1 },
    desc: 'Neon green. +30 Energy. Teeth optional.',
  },
  {
    id: 'tvDinner',
    name: 'Hungry-Man XL TV Dinner',
    icon: ':tv-dinner:',
    price: 5.49,
    give: { tvDinner: 1 },
    desc: 'Microwaved behind the counter. +60 Hunger, +5 Health.',
  },
  {
    id: 'pizza',
    name: 'Whole Gas Station Pizza',
    icon: ':pizza:',
    price: 7.99,
    give: { pizza: 1 },
    desc: 'Been under the heat lamp since lunch. Which lunch? +75 Hunger.',
  },
  {
    id: 'chicken',
    name: '12-Piece Bucket of Chicken',
    icon: ':fried-chicken:',
    price: 12.99,
    give: { chicken: 1 },
    desc: 'Fills you all the way up. +10 Health. Grease included at no extra charge.',
  },
  {
    id: 'bigEnergy',
    name: "Liquid Lightnin' 24oz",
    icon: ':energy-drink:',
    price: 3.99,
    give: { bigEnergy: 1 },
    desc: '+55 Energy. Warning label is longer than the ingredients.',
  },
  {
    id: 'rocketFuel',
    name: 'Redneck Rocket Fuel Shot',
    icon: ':energy-shot:',
    price: 6.99,
    give: { rocketFuel: 1 },
    desc: '+85 Energy and a little sober. -8 Health. Not FDA approved, or FDA aware.',
  },
  {
    id: 'flashlight',
    name: 'Mag-Lite Flashlight',
    icon: ':flashlight:',
    price: 9,
    give: { flashlight: 1 },
    desc: 'For the walk home from the Rusty Spur. L turns it on.',
  },
  {
    id: 'headlamp',
    name: "Coon-Huntin' Headlamp",
    icon: ':headlamp:',
    price: 16,
    give: { headlamp: 1 },
    desc: 'Brighter, wider and hands free. L turns it on.',
  },
  {
    id: 'scratcher',
    name: 'Lucky 7s Scratch-Off',
    icon: ':scratcher:',
    price: 2,
    give: { scratcher: 1 },
    desc: 'Top prize $500. Odds: "yes."',
  },
  {
    id: 'scratchCow',
    name: 'Cash Cow Scratch-Off',
    icon: ':cow:',
    price: 5,
    give: { scratchCow: 1 },
    desc: 'Top prize $2,500.',
  },
  {
    id: 'scratchGator',
    name: 'Gator Gold Scratch-Off',
    icon: ':gator:',
    price: 10,
    give: { scratchGator: 1 },
    desc: 'Top prize $10,000.',
  },
  {
    id: 'scratchMega',
    name: 'Trailer Park Millionaire',
    icon: ':gem:',
    price: 20,
    give: { scratchMega: 1 },
    desc: 'Top prize $100,000. One in a hundred thousand. Probably.',
  },
  {
    id: 'cigs',
    name: 'Paul Mauls, Pack of 20',
    icon: ':smokes:',
    price: 8,
    give: { cigs: 20 },
    desc: 'The clerk has your brand behind the counter before you ask.',
  },
  {
    id: 'fireworks',
    name: 'Freedom Pack Fireworks',
    icon: ':fireworks:',
    price: 15,
    give: { fireworks: 1 },
    desc: 'Legal in this county. Probably.',
  },
  {
    id: 'mash',
    name: 'Sweet Feed (for "horses")',
    icon: ':corn:',
    price: 6,
    give: { mash: 1 },
    desc: "Corn, sugar, yeast. The clerk doesn't ask what horse.",
  },
];

/** The Booze Barn: cheaper beer by the case, and the hard stuff. */
export const BOOZE_BARN_STOCK: readonly ShopEntry[] = [
  {
    id: 'beer30',
    name: 'Nattie Lite 30-Rack',
    icon: ':beer-case:',
    price: 22,
    give: { beer: 30 },
    desc: 'The suitcase. A man of means.',
  },
  {
    id: 'beer12b',
    name: 'Nattie Lite 12-Pack (Cold)',
    icon: ':beer-case:',
    price: 10,
    give: { beer: 12 },
    desc: 'Actually cold. The Booze Barn takes its job seriously.',
  },
  {
    id: 'wine',
    name: "Bone's Farm Strawberry Hill",
    icon: ':strawberry-hill:',
    price: 4,
    give: { wine: 1 },
    desc: 'Screw top, for your convenience. +35 Buzz.',
  },
  {
    id: 'whiskey',
    name: 'Fire Bawl Shooters (3)',
    icon: ':fire-bawl:',
    price: 5,
    give: { whiskey: 3 },
    desc: 'Tiny bottles. Big decisions. +22 Buzz each.',
  },
  {
    id: 'cigsB',
    name: 'Paul Mauls, Pack of 20',
    icon: ':smokes:',
    price: 7.5,
    give: { cigs: 20 },
    desc: "Fifty cents cheaper than the gas station. That's the Booze Barn promise.",
  },
];
